import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Calendar, AlertCircle } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

export default function Returns() {
  const { user } = useAuth();
  const [orders, setOrders] = useState({ orders: [], bespoke: [] });
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({ order_id: "", type: "return", reason: "Size doesn't fit", photo_urls: [], issue_detail: "", pickup_slot: "10am-1pm", pickup_address: { line1: "", line2: "", city: "", state: "", pincode: "", phone: "", label: "Home" } });

  const refresh = async () => {
    const [o, r] = await Promise.all([api.get("/orders"), api.get("/returns")]);
    setOrders(o.data); setMine(r.data);
  };
  useEffect(() => { if (user) refresh(); }, [user]);

  if (!user) return <div className="sq-container py-20"><Link to="/auth" className="sq-btn-primary">Sign in</Link></div>;

  const submit = async () => {
    if (!form.order_id) return toast.error("Choose an order");
    try {
      const photos = Array.isArray(form.photo_urls) ? form.photo_urls : String(form.photo_urls || "").split(",").map((s) => s.trim()).filter(Boolean);
      const payload = { ...form, photo_urls: photos };
      const { data } = await api.post("/returns", payload);
      toast.success(`${data.type === "alteration" ? "Alteration" : "Return"} ${data.id} scheduled`);
      setForm({ ...form, reason: "", issue_detail: "", photo_urls: [] });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const eligible = [
    ...orders.orders.map((o) => ({ id: o.id, label: `${o.id} — ${o.items?.length || 0} items`, type: "return" })),
    ...orders.bespoke.map((b) => ({ id: b.id, label: `${b.id} — Bespoke ${b.garment_type} (alteration only)`, type: "alteration" })),
  ];

  return (
    <main className="sq-container py-12" data-testid="returns-page">
      <div className="sq-eyebrow text-squire-jeans">7-day window</div>
      <h1 className="font-display text-5xl md:text-6xl mt-2">Returns & Alterations</h1>
      <p className="text-[#4B5563] mt-3 max-w-xl">Bespoke garments are non-returnable but eligible for complimentary alterations. Ready-made items can be returned within 7 days.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-12">
        <section className="border border-black/10 p-6">
          <div className="sq-eyebrow flex items-center gap-2"><AlertCircle size={12} /> Request</div>
          <label className="block mt-4">
            <span className="sq-eyebrow text-[10px]">Order</span>
            <select data-testid="ret-order" value={form.order_id} onChange={(e) => { const x = eligible.find((y) => y.id === e.target.value); setForm({ ...form, order_id: e.target.value, type: x?.type || "return" }); }} className="w-full bg-transparent border-b border-black/30 outline-none py-2 text-sm">
              <option value="">Select…</option>
              {eligible.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </label>
          <label className="block mt-4">
            <span className="sq-eyebrow text-[10px]">Type</span>
            <div className="flex gap-2 mt-2">
              {["return", "alteration"].map((t) => (
                <button key={t} data-testid={`ret-type-${t}`} onClick={() => setForm({ ...form, type: t })} className={`px-3 py-1 text-xs border ${form.type === t ? "border-squire-jeans bg-squire-jeans text-white" : "border-black/20"}`}>{t}</button>
              ))}
            </div>
          </label>
          {[["reason", "Issue"], ["issue_detail", "Details"]].map(([k, l]) => (
            <label key={k} className="block mt-4"><span className="sq-eyebrow text-[10px]">{l}</span>
              <input data-testid={`ret-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-2 text-sm" />
            </label>
          ))}
          <div className="mt-4">
            <ImageUploader value={form.photo_urls} onChange={(urls) => setForm({ ...form, photo_urls: urls })} multiple={true} max={4} label="Upload photos of the issue (up to 4)" testid="ret-photos" />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {[["line1", "Pickup Line 1"], ["city", "City"], ["pincode", "Pincode"], ["phone", "Phone"]].map(([k, l]) => (
              <label key={k} className="block"><span className="sq-eyebrow text-[10px]">{l}</span>
                <input data-testid={`ret-pickup-${k}`} value={form.pickup_address[k]} onChange={(e) => setForm({ ...form, pickup_address: { ...form.pickup_address, [k]: e.target.value } })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
              </label>
            ))}
          </div>
          <label className="block mt-4">
            <span className="sq-eyebrow text-[10px] flex items-center gap-1"><Calendar size={10} /> Pickup Slot</span>
            <select data-testid="ret-slot" value={form.pickup_slot} onChange={(e) => setForm({ ...form, pickup_slot: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-2 text-sm">
              {["10am-1pm", "1pm-4pm", "4pm-7pm"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
          <button data-testid="ret-submit" onClick={submit} className="sq-btn-primary w-full mt-6">Book Pickup</button>
        </section>

        <section>
          <div className="sq-eyebrow mb-3">My Requests · {mine.length}</div>
          <div className="space-y-3 max-h-[640px] overflow-y-auto sq-scroll">
            {mine.length === 0 && <div className="text-sm text-[#4B5563]">No requests yet.</div>}
            {mine.map((r) => (
              <div key={r.id} className="border border-black/10 p-4" data-testid={`ret-row-${r.id}`}>
                <div className="flex justify-between"><div className="font-display">{r.id}</div><div className="sq-eyebrow">{r.type.toUpperCase()} · {r.status}</div></div>
                <div className="sq-eyebrow text-[10px] mt-1">{r.order_id} · {r.reason}</div>
                {r.refund_id && <div className="text-xs text-squire-jeans mt-2">Refund: {r.refund_id}</div>}
              </div>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}
